"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowRight, Loader2 } from "lucide-react";
import { StepCard } from "./StepCard";
import type { StepStatus, SwapQuote } from "./types";

interface Step4Props {
  status: StepStatus;
  isConnected: boolean;
  activeAccountId: string;
  xlmAmount: string;
  swapXlmInput: string;
  setSwapXlmInput: (v: string) => void;
  swapQuote: SwapQuote;
  onSwap: () => void;
  onGoBack?: () => void;
}

export function Step4SwapTokens({
  status,
  isConnected,
  activeAccountId,
  xlmAmount,
  swapXlmInput,
  setSwapXlmInput,
  swapQuote,
  onSwap,
  onGoBack,
}: Step4Props) {
  const effectiveXlm = parseFloat(swapXlmInput) || (parseFloat(xlmAmount) || 10) / 2;
  const isProcessing = status === "in-progress";

  return (
    <StepCard
      stepNumber={4}
      title="Swap XLM to USDC"
      description="Convert XLM to USDC via the Hoops router"
      status={status}
      onGoBack={onGoBack}
      completedSummary={
        <span>Swapped {String(effectiveXlm)} XLM to USDC at market rate</span>
      }
    >
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="text-sm text-muted-foreground whitespace-nowrap">XLM to swap:</div>
          <Input
            type="number"
            value={swapXlmInput}
            onChange={(e) => { setSwapXlmInput(e.target.value); }}
            placeholder={String((parseFloat(xlmAmount) || 10) / 2)}
            className="w-28 font-mono"
            min="0.1"
            step="0.1"
          />
          <div className="text-sm font-medium">XLM</div>
        </div>

        {/* Token display */}
        <div className="flex items-center justify-center gap-4 py-3">
          <div className="text-center">
            <div className="text-2xl font-bold font-mono">{String(effectiveXlm)}</div>
            <div className="text-sm text-muted-foreground">XLM</div>
          </div>
          <ArrowRight className="w-5 h-5 text-muted-foreground" />
          <div className="text-center">
            <div className="text-2xl font-bold font-mono">
              {swapQuote.loading ? (
                <Loader2 className="w-5 h-5 animate-spin inline" />
              ) : swapQuote.estimatedUsdc ? (
                `~${swapQuote.estimatedUsdc}`
              ) : (
                "?"
              )}
            </div>
            <div className="text-sm text-muted-foreground">USDC</div>
            <div className="text-xs text-muted-foreground">at market rate</div>
          </div>
        </div>

        <div className="text-xs text-muted-foreground space-y-0.5 bg-muted rounded-md p-3">
          <div>Route: Account &rarr; Router &rarr; Soroswap Adapter &rarr; Pool</div>
          <div>Output determined by pool reserves (constant-product AMM)</div>
        </div>

        <Button
          onClick={onSwap}
          disabled={!activeAccountId || !isConnected || isProcessing}
          className="w-full"
          variant="secondary"
        >
          {isProcessing ? "Swapping..." : `Swap ${String(effectiveXlm)} XLM to USDC`}
        </Button>
      </div>
    </StepCard>
  );
}
